// Import necessary modules from Three.js and custom utilities
import * as THREE from 'three';
import { OrbitControls } from "../Utils/OrbitControls";
import { LABELS } from "../Global/variables";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer";
import { LINES, OBSTACLE } from "../Global/materials";
import { createLabelDiv } from "../Global/functions";
import { initSmartGuides } from '../Segments/Snaps/GuidingLines';

// SceneManager class (singleton)
class SceneManager {
    constructor() {
        // Singleton pattern: ensure only one instance of SceneManager exists
        if (!SceneManager.instance) {
            this.initialize();
            SceneManager.instance = this;
        }
        return SceneManager.instance;
    }

    // Initializes the scene, camera, renderer, lights, and controls
    initialize() {
        // Create a new Three.js scene
        this.scene = new THREE.Scene();

        // Set scene height considering a UI offset
        this.height = window.innerHeight - 92.8;

        // Setup perspective camera
        this.camera = new THREE.PerspectiveCamera(
            45,
            window.innerWidth / this.height,
            0.1,
            5000
        );
        this.camera.up.set(0, 0, 1); // Z is up for this application

        // Create WebGL renderer
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        document.body.appendChild(this.renderer.domElement); // Attach renderer to DOM
        this.renderer.setClearColor(0x000000); // Background color
        this.renderer.setSize(window.innerWidth, this.height); // Size of the render area

        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Optional: softer shadows

        // Initialize orbit controls for camera navigation
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.addEventListener('start', this.onStartCameraRotation.bind(this));
        this.controls.addEventListener('end', this.onEndCameraRotation.bind(this));

        // Setup lighting
        this.directionalLight = new THREE.DirectionalLight(0xffffff, 5);
        this.ambientLight = new THREE.AmbientLight(0xffffff, 0.8);

        // Will be initialized later
        this.labelRenderer = null;

        // Configure and populate the scene
        this.setUpScene();
    }

    // Set up the complete scene
    setUpScene() {
        this.setControl(); // Configure camera and control settings
        this.setUpLabelRenderer(); // Setup 2D label renderer
        this.setUpRefrenceAngle(); // Setup angle label
        this.setUpLengthLabel();
        this.setUpRefrenceLine(); // Add reference measurement line
        this.setDirectionalLights(); // Add main directional light
        this.setAmbientLight(); // Add ambient light
        initSmartGuides(this.scene)

        // Add geometric references to the scene
        this.scene.add(OBSTACLE.circle);
        this.scene.add(OBSTACLE.box);
    }

    // Configure camera controls
    setControl() {
        this.camera.position.set(0, 0, 50); // Initial camera position
        this.controls.listenToKeyEvents(window); // Listen to keyboard inputs

        // Customize mouse buttons
        this.controls.mouseButtons = {
            LEFT: null,
            MIDDLE: THREE.MOUSE.PAN,
            RIGHT: THREE.MOUSE.ROTATE,
        };

        this.controls.zoomToCursor = true;
        this.controls.rotateSpeed = 1;
        this.controls.enablePan = true;
        this.controls.maxPolarAngle = Math.PI / 2; // Limit vertical rotation to top view
        this.controls.target.set(0, 0, 0); // Look at center
        this.controls.update();

        // Optionally add axis helper
        // const axis = new THREE.AxesHelper(250);
        // this.scene.add(axis);
    }

    // Add directional light source to the scene
    setDirectionalLights() {
        this.directionalLight.position.set(50, 50, 50);
        this.directionalLight.castShadow = true;
        this.directionalLight.shadow.mapSize.width = 512;
        this.directionalLight.shadow.mapSize.height = 512;
        this.directionalLight.shadow.camera.near = 0.5;
        this.directionalLight.shadow.camera.far = 500;

        this.scene.add(this.directionalLight);
    }

    // Add ambient lighting to ensure scene visibility
    setAmbientLight() {
        this.scene.add(this.ambientLight);
    }

    // Set up CSS2D renderer for HTML-based labels
    setUpLabelRenderer() {
        this.labelRenderer = new CSS2DRenderer();
        this.labelRenderer.setSize(window.innerWidth, this.height);

        // Style the label renderer container
        this.labelRenderer.domElement.style.position = 'fixed';
        this.labelRenderer.domElement.style.top = '72px';
        this.labelRenderer.domElement.style.pointerEvents = 'none';
        this.labelRenderer.domElement.style.fontSize = '14px';
        this.labelRenderer.domElement.style.textShadow = '1px 1px 3px rgba(0, 0, 0, 0.8)';
        this.labelRenderer.domElement.style.webkitTextStroke = '0.8px #ffffff';
        this.labelRenderer.domElement.style.transform = 'rotateX(0) rotateY(0) rotateZ(0)';
    }

    // Add a line to represent reference distance/measurement
    setUpRefrenceLine() {
        LINES.referenceLine.computeLineDistances();
        LINES.referenceLine.visible = false;
        this.scene.add(LINES.referenceLine);
    }

    // Add a label to show angle values
    setUpRefrenceAngle() {
        LABELS.angle = createLabelDiv("", "0°");
        LABELS.angle.visible = false;
        this.scene.add(LABELS.angle);
    }

    setUpLengthLabel() {
        LABELS.length = createLabelDiv("", "0.00 m");
        LABELS.length.visible = false;
        this.scene.add(LABELS.length);
    }

    // Handle window resize events to update camera and renderer
    handleResize() {
        this.camera.aspect = window.innerWidth / this.height;
        this.camera.updateProjectionMatrix();

        // Resize both 3D and 2D renderers
        this.renderer.setSize(window.innerWidth, window.innerHeight - 92.8);
        this.renderer.render(this.scene, this.camera);
        this.labelRenderer.setSize(window.innerWidth, this.height);
    }

    // Camera control event handlers
    onStartCameraRotation() {
        this.isCameraRotating = true;
    }

    onEndCameraRotation() {
    this.isCameraRotating = false;
        //these below logs are just for testing purposes, feel free to remove later
   
}


    // Public method to access scene elements
    getSceneObjects() {
        return {
            scene: this.scene,
            camera: this.camera,
            renderer: this.renderer,
            controls: this.controls,
            labelRenderer: this.labelRenderer,
        };
    }
}

// Export a singleton instance of SceneManager
export const sceneManager = new SceneManager();
